import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, RefreshCw, Settings, Receipt, FileSpreadsheet, CheckCircle, Clock, AlertCircle, Euro, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800', icon: Clock },
  PENDING: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  APPROVED: { label: 'Approvata', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  PAID: { label: 'Pagata', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  CANCELLED: { label: 'Annullata', color: 'bg-red-100 text-red-800', icon: AlertCircle }
};

const LEDGER_STATUS = {
  ACCRUED: { label: 'Maturata', color: 'bg-yellow-100 text-yellow-800' },
  INCLUDED_IN_F24: { label: 'In F24', color: 'bg-blue-100 text-blue-800' },
  PAID_VIA_F24: { label: 'Versata', color: 'bg-green-100 text-green-800' },
  ADJUSTED: { label: 'Rettificata', color: 'bg-purple-100 text-purple-800' }
};

const F24_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  GENERATED: { label: 'Generato', color: 'bg-blue-100 text-blue-800' },
  PAID: { label: 'Pagato', color: 'bg-green-100 text-green-800' }
};

export const Tax = () => {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [f24Forms, setF24Forms] = useState([]);
  const [settings, setSettings] = useState({});
  const [dashboard, setDashboard] = useState({});
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [withholdingDialogOpen, setWithholdingDialogOpen] = useState(false);
  const [f24DialogOpen, setF24DialogOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedF24, setSelectedF24] = useState(null);
  const [saving, setSaving] = useState(false);
  const { token, selectedCondominium } = useAuth();
  
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const canEdit = ['ADMIN', 'ACCOUNTANT'].includes(userRole);
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  
  const [withholdingForm, setWithholdingForm] = useState({
    withholding_applicable: true,
    withholding_rate: 4.0,
    withholding_base_rule: 'TAXABLE_ONLY'
  });
  
  const [settingsForm, setSettingsForm] = useState({
    default_rate: 4.0,
    default_base_rule: 'TAXABLE_ONLY',
    default_professional_rate: 20.0,
    due_day: 16,
    allow_manual_override: true,
    tributo_code_condomini: '1040'
  });
  
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const condoId = selectedCondominium?.condominium?.id;

  const fetchData = useCallback(async () => {
    if (!token || !condoId) return;
    setLoading(true);
    try {
      // Fetch expenses with withholding
      const expRes = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/expenses?year=${selectedYear}&withholding_only=false`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (expRes.ok) {
        const data = await expRes.json();
        setExpenses(data.expenses || []);
        setSummary(data.summary || {});
      }
      
      // Fetch F24 forms
      const f24Res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/f24?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (f24Res.ok) {
        const data = await f24Res.json();
        setF24Forms(data.forms || []);
      }
      
      // Fetch settings
      const setRes = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (setRes.ok) {
        const data = await setRes.json();
        setSettings(data);
        setSettingsForm(data);
      }
      
      // Fetch dashboard
      const dashRes = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/dashboard?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (dashRes.ok) {
        setDashboard(await dashRes.json());
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token, condoId, selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateWithholding = async () => {
    if (!selectedExpense) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('withholding_applicable', withholdingForm.withholding_applicable.toString());
      formData.append('withholding_rate', withholdingForm.withholding_rate.toString());
      formData.append('withholding_base_rule', withholdingForm.withholding_base_rule);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/expenses/${selectedExpense.id}/withholding`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Ritenuta aggiornata');
        setWithholdingDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore aggiornamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(settingsForm).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/settings`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Impostazioni salvate');
        setSettingsDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateF24 = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/f24/generate`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('F24 generato con successo');
        setF24DialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore generazione F24');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadF24Pdf = async (f24Id) => {
    window.open(`${API_URL}/api/condominiums/${condoId}/withholding/f24/${f24Id}/pdf?token=${token}`, '_blank');
  };

  const handleMarkF24Paid = async () => {
    if (!selectedF24) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('payment_date', paymentDate);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/withholding/f24/${selectedF24.id}/mark-paid`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('F24 registrato come pagato');
        setMarkPaidDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadReport = async (format, type = 'monthly') => {
    const url = type === 'monthly'
      ? `${API_URL}/api/condominiums/${condoId}/withholding/reports/monthly?year=${selectedYear}&month=${selectedMonth}&format=${format}`
      : `${API_URL}/api/condominiums/${condoId}/withholding/reports/annual?year=${selectedYear}&format=${format}`;
    
    window.open(`${url}&token=${token}`, '_blank');
    toast.success(`Download ${format} avviato`);
  };

  const openWithholdingDialog = (expense) => {
    setSelectedExpense(expense);
    setWithholdingForm({
      withholding_applicable: expense.withholding_applicable || false,
      withholding_rate: expense.withholding_rate || settings.default_rate || 4.0,
      withholding_base_rule: expense.withholding_base_rule || 'TAXABLE_ONLY'
    });
    setWithholdingDialogOpen(true);
  };

  const openMarkPaidDialog = (f24) => {
    setSelectedF24(f24);
    setMarkPaidDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Ritenute e 770"
        subtitle="Adempimenti fiscali"
        actions={
          canEdit ? (
            <Button variant="outline" className="bg-white text-primary hover:bg-white/90" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Impostazioni
            </Button>
          ) : null
        }
      />

      {/* Stats Dashboard */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Fatture con Ritenuta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.total_expenses_with_withholding || 0}</div>
            <p className="text-xs text-muted-foreground">Anno {selectedYear}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Ritenute Maturate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatCurrency(dashboard.total_accrued)}</div>
            <p className="text-xs text-muted-foreground">Da versare</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              In F24
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(dashboard.total_included_in_f24)}</div>
            <p className="text-xs text-muted-foreground">F24: {dashboard.f24_forms_count || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Versate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(dashboard.total_paid)}</div>
            <p className="text-xs text-muted-foreground">Pagati: {dashboard.f24_paid_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Period Selection */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Anno:</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Mese:</Label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <SelectItem key={m} value={m.toString()}>
                      {new Date(2025, m-1, 1).toLocaleDateString('it-IT', {month: 'long'})}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => fetchData()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="expenses" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="expenses">
            <Receipt className="h-4 w-4 mr-2" />
            Fatture
          </TabsTrigger>
          <TabsTrigger value="reports">
            <FileText className="h-4 w-4 mr-2" />
            Report
          </TabsTrigger>
          <TabsTrigger value="f24">
            <Euro className="h-4 w-4 mr-2" />
            F24
          </TabsTrigger>
          <TabsTrigger value="annual">
            <TrendingUp className="h-4 w-4 mr-2" />
            770
          </TabsTrigger>
        </TabsList>

        {/* EXPENSES TAB */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader>
              <CardTitle>Fatture con Ritenuta</CardTitle>
              <CardDescription>
                Gestisci le ritenute d'acconto sulle fatture. Totale ritenute: {formatCurrency(summary.total_withholding)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expenses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>N. Fattura</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Imponibile</TableHead>
                      <TableHead className="text-right">Ritenuta</TableHead>
                      <TableHead className="text-right">Netto</TableHead>
                      <TableHead>Stato</TableHead>
                      {canEdit && <TableHead className="text-right">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((expense) => {
                      const statusInfo = STATUS_BADGES[expense.status] || STATUS_BADGES.PENDING;
                      return (
                        <TableRow key={expense.id}>
                          <TableCell className="font-medium">{expense.supplier_name || '-'}</TableCell>
                          <TableCell>{expense.invoice_number || '-'}</TableCell>
                          <TableCell>{formatDate(expense.invoice_date)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(expense.taxable_amount)}</TableCell>
                          <TableCell className="text-right">
                            {expense.withholding_applicable ? (
                              <span className="font-medium text-red-600">
                                {formatCurrency(expense.withholding_amount)}
                                <span className="text-xs ml-1">({expense.withholding_rate}%)</span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(expense.net_to_pay)}</TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openWithholdingDialog(expense)}
                              >
                                {expense.withholding_applicable ? 'Modifica' : 'Applica'}
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna fattura trovata per il periodo selezionato</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTS TAB */}
        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle>Report Mensile Ritenute</CardTitle>
              <CardDescription>
                {canEdit ? `Genera il report mensile delle ritenute per ${selectedMonth.toString().padStart(2, '0')}/${selectedYear}` : `Riepilogo ritenute per ${selectedMonth.toString().padStart(2, '0')}/${selectedYear}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {canEdit && (
                <div className="flex gap-2">
                  <Button onClick={() => handleDownloadReport('PDF', 'monthly')}>
                    <Download className="h-4 w-4 mr-2" />
                    Scarica PDF
                  </Button>
                  <Button variant="outline" onClick={() => handleDownloadReport('CSV', 'monthly')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Scarica CSV
                  </Button>
                </div>
              )}
              <div className="border rounded-lg p-4 bg-muted/50">
                <h4 className="font-medium mb-2">Anteprima Riepilogo</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Totale Imponibile:</span>
                    <p className="font-medium">{formatCurrency(summary.total_taxable)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Totale Ritenute:</span>
                    <p className="font-medium text-red-600">{formatCurrency(summary.total_withholding)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Totale Netto:</span>
                    <p className="font-medium">{formatCurrency(summary.total_net_to_pay)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* F24 TAB */}
        <TabsContent value="f24">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Modelli F24</CardTitle>
                <CardDescription>
                  {canEdit ? 'Genera e gestisci i modelli F24 per il versamento delle ritenute' : 'Consulta i modelli F24 per il versamento delle ritenute'}
                </CardDescription>
              </div>
              {canEdit && (
                <Button onClick={() => setF24DialogOpen(true)}>
                  <Euro className="h-4 w-4 mr-2" />
                  Genera F24
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {f24Forms.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Periodo</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {f24Forms.map((f24) => {
                      const statusInfo = F24_STATUS[f24.payment_status] || F24_STATUS.DRAFT;
                      return (
                        <TableRow key={f24.id}>
                          <TableCell className="font-medium">
                            {f24.month.toString().padStart(2, '0')}/{f24.year}
                          </TableCell>
                          <TableCell>{formatDate(f24.due_date)}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(f24.total_withholding_amount)}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadF24Pdf(f24.id)}
                              title="Scarica PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {canEdit && f24.payment_status !== 'PAID' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openMarkPaidDialog(f24)}
                                title="Registra pagamento"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Euro className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun F24 generato per il {selectedYear}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 770 TAB */}
        <TabsContent value="annual">
          <Card>
            <CardHeader>
              <CardTitle>Export Modello 770</CardTitle>
              <CardDescription>
                {canEdit ? `Esporta i dati annuali per la compilazione del Modello 770 - Anno ${selectedYear}` : `Riepilogo annuale ritenute - Anno ${selectedYear}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {canEdit && (
                <div className="flex gap-2">
                  <Button onClick={() => handleDownloadReport('CSV', 'annual')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export 770 (CSV)
                  </Button>
                  <Button variant="outline" onClick={() => handleDownloadReport('PDF', 'annual')}>
                    <Download className="h-4 w-4 mr-2" />
                    Report Annuale (PDF)
                  </Button>
                </div>
              )}
              <div className="border rounded-lg p-4 bg-muted/50">
                <h4 className="font-medium mb-2">Riepilogo Annuale {selectedYear}</h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Fatture:</span>
                    <p className="font-medium">{dashboard.total_expenses_with_withholding || 0}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Maturate:</span>
                    <p className="font-medium text-yellow-600">{formatCurrency(dashboard.total_accrued)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">In F24:</span>
                    <p className="font-medium text-blue-600">{formatCurrency(dashboard.total_included_in_f24)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Versate:</span>
                    <p className="font-medium text-green-600">{formatCurrency(dashboard.total_paid)}</p>
                  </div>
                </div>
              </div>
              {canEdit && (
                <div className="text-sm text-muted-foreground">
                  <p>Il file CSV contiene i dati dei fornitori nel formato richiesto per la compilazione del Modello 770:</p>
                  <ul className="list-disc ml-4 mt-2">
                    <li>Codice Fiscale del percipiente</li>
                    <li>Denominazione</li>
                    <li>Compensi corrisposti</li>
                    <li>Ritenute operate</li>
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Withholding Dialog */}
      <Dialog open={withholdingDialogOpen} onOpenChange={setWithholdingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gestione Ritenuta</DialogTitle>
            <DialogDescription>
              {selectedExpense && `Fattura: ${selectedExpense.invoice_number || selectedExpense.description}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Label>Applica Ritenuta</Label>
              <Switch
                checked={withholdingForm.withholding_applicable}
                onCheckedChange={(checked) => setWithholdingForm({...withholdingForm, withholding_applicable: checked})}
              />
            </div>
            {withholdingForm.withholding_applicable && (
              <>
                <div className="space-y-2">
                  <Label>Aliquota (%)</Label>
                  <Select 
                    value={withholdingForm.withholding_rate.toString()} 
                    onValueChange={(v) => setWithholdingForm({...withholdingForm, withholding_rate: parseFloat(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4% - Condomini (pulizie, manutenzione)</SelectItem>
                      <SelectItem value="20">20% - Professionisti</SelectItem>
                      <SelectItem value="23">23% - Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Base di Calcolo</Label>
                  <Select 
                    value={withholdingForm.withholding_base_rule} 
                    onValueChange={(v) => setWithholdingForm({...withholdingForm, withholding_base_rule: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TAXABLE_ONLY">Solo Imponibile</SelectItem>
                      <SelectItem value="GROSS_TOTAL">Totale Lordo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedExpense && (
                  <div className="border rounded-lg p-3 bg-muted/50 text-sm">
                    <div className="flex justify-between">
                      <span>Imponibile:</span>
                      <span>{formatCurrency(selectedExpense.taxable_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Ritenuta ({withholdingForm.withholding_rate}%):</span>
                      <span className="font-medium text-red-600">
                        {formatCurrency(selectedExpense.taxable_amount * withholdingForm.withholding_rate / 100)}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithholdingDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleUpdateWithholding} disabled={saving}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate F24 Dialog */}
      <Dialog open={f24DialogOpen} onOpenChange={setF24DialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Genera F24</DialogTitle>
            <DialogDescription>
              Genera il modello F24 per il versamento delle ritenute del mese selezionato
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mese</Label>
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>
                        {new Date(2025, m-1, 1).toLocaleDateString('it-IT', {month: 'long'})}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anno</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2023, 2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Verranno incluse tutte le ritenute maturate (stato ACCRUED) per il periodo selezionato.</p>
              <p className="mt-2">Scadenza versamento: 16 del mese successivo.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setF24DialogOpen(false)}>Annulla</Button>
            <Button onClick={handleGenerateF24} disabled={saving}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Genera F24
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Pagamento F24</DialogTitle>
            <DialogDescription>
              {selectedF24 && `F24 ${selectedF24.month.toString().padStart(2, '0')}/${selectedF24.year} - ${formatCurrency(selectedF24.total_withholding_amount)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data Pagamento</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleMarkF24Paid} disabled={saving}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Conferma Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Impostazioni Ritenute</DialogTitle>
            <DialogDescription>
              Configura le impostazioni predefinite per il calcolo delle ritenute
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Aliquota Default (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settingsForm.default_rate}
                  onChange={(e) => setSettingsForm({...settingsForm, default_rate: parseFloat(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Aliquota Professionisti (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settingsForm.default_professional_rate}
                  onChange={(e) => setSettingsForm({...settingsForm, default_professional_rate: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Base di Calcolo Default</Label>
              <Select 
                value={settingsForm.default_base_rule} 
                onValueChange={(v) => setSettingsForm({...settingsForm, default_base_rule: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TAXABLE_ONLY">Solo Imponibile</SelectItem>
                  <SelectItem value="GROSS_TOTAL">Totale Lordo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Giorno Scadenza F24</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={settingsForm.due_day}
                  onChange={(e) => setSettingsForm({...settingsForm, due_day: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Codice Tributo</Label>
                <Input
                  value={settingsForm.tributo_code_condomini}
                  onChange={(e) => setSettingsForm({...settingsForm, tributo_code_condomini: e.target.value})}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Consenti override manuale</Label>
              <Switch
                checked={settingsForm.allow_manual_override}
                onCheckedChange={(checked) => setSettingsForm({...settingsForm, allow_manual_override: checked})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Salva Impostazioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
